import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/estates", label: "Estates" },
  { to: "/events", label: "Events" },
  { to: "/business", label: "Business" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { totals, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();
  // Only Home page has a dark hero behind the transparent navbar
  const onDarkHero = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || !onDarkHero ? "bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[#EBE3D5]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between h-24">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
          <picture>
            <source srcSet="/logo.webp" type="image/webp" />
            <img
              src="/logo.png"
              alt="SIGA Coffee — A Curated Coffee Experience"
              width="800"
              height="174"
              className="h-12 md:h-14 w-auto transition-[filter] duration-300"
              style={{ filter: onDarkHero ? "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.55))" : "none" }}
            />
          </picture>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.2em] font-medium link-underline transition-colors ${
                  isActive
                    ? "text-[#C85A32]"
                    : onDarkHero
                    ? "text-[#FDFBF7]"
                    : "text-[#2C1E16]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className={`flex items-center gap-4 transition-colors ${onDarkHero ? "text-[#FDFBF7]" : "text-[#2C1E16]"}`}>
          <Link
            to={user && user.role === "admin" ? "/admin" : user ? "/account" : "/login"}
            data-testid="nav-account-icon"
            className="p-2 hover:text-[#C85A32] transition-colors"
            aria-label="Account"
          >
            <User size={18} strokeWidth={1.5} />
          </Link>
          <button
            data-testid="cart-open-button"
            onClick={() => setOpen(true)}
            className="p-2 relative hover:text-[#C85A32] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {totals.count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-[#C85A32] text-[#FDFBF7] text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold"
              >
                {totals.count}
              </span>
            )}
          </button>
          <button
            className="md:hidden p-2"
            onClick={() => setMobile((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden bg-[#FDFBF7] border-t border-[#EBE3D5] px-6 py-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setMobile(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="block py-3 text-sm uppercase tracking-[0.2em]"
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
